/**
 * Global workspace store: the active salon (root company) and the chosen branch within it.
 *
 * - `branch` = '' means "All branches" of the active salon (tenant-wide view).
 * - Persisted per user + active root company in localStorage.
 * - Restored when the session boot loads/changes; ignored if the restored branch
 *   is not in the current user's ready branches for that salon.
 */

import { computed, reactive, watch } from 'vue'
import { session } from './session'
import { confirmDiscard } from './composables/useDirtyGuard'
import type { BootBranch } from './types/api'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WorkspaceBranch {
  name: string
  label: string
}

export interface WorkspaceState {
  /** Empty string = "All branches" of the active salon. */
  branch: string
}

// ---------------------------------------------------------------------------
// Reactive state
// ---------------------------------------------------------------------------

const state = reactive<WorkspaceState>({
  branch: '',
})

// ---------------------------------------------------------------------------
// Persistence key helpers
// ---------------------------------------------------------------------------

function persistenceKey(): string | null {
  const user = session.boot?.user
  const company = session.boot?.company
  if (!user || !company) return null
  return `glamora_workspace:${user}:${company}`
}

// ---------------------------------------------------------------------------
// Branch helpers (derived from boot)
// ---------------------------------------------------------------------------

/**
 * Returns the ready branches for the active salon, plus an "All branches"
 * pseudo-choice when there is more than one ready branch.
 *
 * For a branch-scoped user (boot.companies contains only that branch),
 * the only choice is that branch (no "All branches").
 */
export function workspaceBranches(): WorkspaceBranch[] {
  const boot = session.boot
  if (!boot) return []

  const activeRoot = boot.company
  if (!activeRoot) return []

  // Branch-scoped user: boot.companies is the branch itself, boot.branches may be empty.
  // The server enforces scope; such a user sees only their branch.
  const isBranchScoped =
    boot.companies?.length === 1 &&
    boot.branches?.some((b) => b.name === boot.companies[0] && b.ready)

  if (isBranchScoped) {
    const branch = boot.branches?.find(
      (b) => b.name === boot.companies[0] && b.ready,
    )
    if (branch) {
      return [{ name: branch.name, label: branchLabel(branch) }]
    }
    // Fallback: the company name from boot.companies
    return [{ name: boot.companies[0], label: boot.companies[0] }]
  }

  // Salon-scoped user: filter boot.branches by ready && parent_company === activeRoot
  const readyBranches = (boot.branches ?? [])
    .filter(
      (b): b is BootBranch & { ready: 1 } =>
        b.ready === 1 && b.parent_company === activeRoot,
    )
    .map((b) => ({ name: b.name, label: branchLabel(b) }))

  if (readyBranches.length <= 1) {
    // Zero or one branch: no "All branches" choice, just the branch (or empty)
    return readyBranches
  }

  // Multiple branches: "All branches" + each branch
  return [{ name: '', label: 'All branches' }, ...readyBranches]
}

function branchLabel(b: BootBranch): string {
  return b.branch_name || b.company_name || b.name
}

/** True when there are at least 2 choices (several branches, or All + >=2 branches, or several salon roots). */
export const workspaceHasChoice = computed(() => {
  const choices = workspaceBranches()
  if (choices.length >= 2) return true
  // Also true if there are multiple salon roots in boot.companies
  return (session.boot?.companies?.length ?? 0) > 1
})

// ---------------------------------------------------------------------------
// Persistence: restore on boot change
// ---------------------------------------------------------------------------

function restoreFromStorage(): void {
  const key = persistenceKey()
  if (!key) return
  try {
    const stored = localStorage.getItem(key)
    if (stored === null) return
    const validBranches = workspaceBranches().map((b) => b.name)
    if (validBranches.includes(stored)) {
      state.branch = stored
    } else {
      // Restored branch not valid for this user/salon -> ignore, keep ''
      state.branch = ''
    }
  } catch {
    // localStorage unavailable or corrupted -> ignore
  }
}

function persistToStorage(): void {
  const key = persistenceKey()
  if (!key) return
  try {
    localStorage.setItem(key, state.branch)
  } catch {
    // Ignore quota/exceptions
  }
}

// Watch for boot changes (company switch or fresh login) and restore
watch(
  () => session.boot?.company,
  () => {
    state.branch = ''
    restoreFromStorage()
  },
  { immediate: true },
)

// Also restore when branches become available (boot.branches may arrive after boot.company)
watch(
  () => session.boot?.branches?.length,
  () => {
    restoreFromStorage()
  },
  { immediate: true },
)

// Persist on change
watch(
  () => state.branch,
  () => {
    persistToStorage()
  },
)

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const workspace = state

/**
 * Set the active workspace branch.
 * Returns true if the branch was set, false if the user cancelled (via dirty guard).
 * Checks the dirty guard before switching.
 */
export async function setWorkspaceBranch(name: string): Promise<boolean> {
  const validBranches = workspaceBranches().map((b) => b.name)
  if (!validBranches.includes(name)) {
    return false
  }
  const ok = await confirmDiscard()
  if (!ok) return false
  state.branch = name
  return true
}

/** Reset workspace to "All branches" (empty string). */
export function resetWorkspace(): void {
  state.branch = ''
}

// Needed so this file is treated as a module
export {}
